/**
 * Pick the rarest non-Avatar cards in a deck for use as the deck-tile
 * "fanned cards" overlay. Stored on the deck row at save time so the
 * gallery doesn't have to scan the full card database for every tile,
 * but the tile derives the same list on the fly when the field is
 * missing (older decks saved before this field existed).
 */

const RARITY_RANK = {
  Unique: 4,
  Elite: 3,
  Exceptional: 2,
  Ordinary: 1,
};

function rarityRank(rarity) {
  return RARITY_RANK[rarity] || 0;
}

/**
 * Art for one card of a deck, preferring the printing the deck itself
 * uses so an alt-art copy shows the player's own version.
 * Returns `{ name, imageUrl, rarity }` or null.
 */
export function deckCardArt(deck, cardId, sorceryCards) {
  if (!cardId || !Array.isArray(sorceryCards)) return null;
  const card = sorceryCards.find((c) => c.unique_id === cardId);
  if (!card) return null;
  const entry = deck?.cards?.find((e) => e.cardId === cardId);
  const printing = card.printings?.find((p) => p.unique_id === entry?.printingId) || card.printings?.[0];
  if (!printing?.image_url) return null;
  return { name: card.name, imageUrl: printing.image_url, rarity: card.rarity || '' };
}

export function computeFeaturedCards(deckCards, sorceryCards, count = 3) {
  if (!Array.isArray(deckCards) || !Array.isArray(sorceryCards)) return [];
  const seen = new Set();
  const entries = [];
  for (const entry of deckCards) {
    const id = entry.cardId;
    if (!id || seen.has(id)) continue;
    const card = sorceryCards.find((c) => c.unique_id === id);
    if (!card || card.type === 'Avatar') continue;
    const printing = card.printings?.find((p) => p.unique_id === entry.printingId) || card.printings?.[0];
    const imageUrl = printing?.image_url || '';
    if (!imageUrl || imageUrl.startsWith('blob:')) continue;
    seen.add(id);
    entries.push({
      name: card.name,
      imageUrl,
      rarity: card.rarity || '',
    });
  }
  entries.sort((a, b) => {
    const dr = rarityRank(b.rarity) - rarityRank(a.rarity);
    if (dr !== 0) return dr;
    return (a.name || '').localeCompare(b.name || '');
  });
  return entries.slice(0, count);
}

/**
 * The three fanned cards a deck tile shows, as a fixed-length array of
 * entries or nulls. `deck.showcase.cards` overrides positionally. A
 * null slot keeps the default pick, so changing one card leaves the
 * other two alone and slot indices stay stable for the editor.
 */
export function resolveFeaturedSlots(deck, sorceryCards) {
  const defaults = Array.isArray(deck?.featuredCards) && deck.featuredCards.length > 0
    ? deck.featuredCards.slice(0, 3)
    : computeFeaturedCards(deck?.cards, sorceryCards, 3);
  const picks = deck?.showcase?.cards;
  return [0, 1, 2].map((i) => (
    (Array.isArray(picks) ? deckCardArt(deck, picks[i], sorceryCards) : null) || defaults[i] || null
  ));
}
